import {
    businessName,
    legalName,
    uen,
    branchAddress,
    branchGeo,
    hqAddress,
    hqGeo,
    hqPhone,
    socialProfiles,
} from 'utils/business-info'

/**
 * The canonical business entity for the whole site.
 *
 * Before this existed the same company was declared inline in ~20 JSON-LD
 * template literals with four different addresses, three coordinate pairs and
 * three names - and thirteen of those nodes had no `@id` at all, so each one
 * read as a separate, unidentified business.
 *
 * Every page now emits THIS object, byte-identical, under one `@id`. Anything
 * page-specific (url, priceRange, service name) belongs on a Service node from
 * buildServiceNode() below, never on the business.
 *
 * Deliberately absent: `aggregateRating` and `review`. Google's review-snippet
 * guidelines make a business ineligible for the star feature on reviews it
 * controls about itself, and separately require "Don't aggregate reviews or
 * ratings from other websites" - StringsSG's rating comes from its own Google
 * Business Profile. The rating is shown as visible page content instead.
 */

export const SITE = "https://www.stringssg.com"
export const LOCAL_BUSINESS_ID = `${SITE}/#localbusiness`
export const ORGANIZATION_ID = `${SITE}/#organization`
export const WEBSITE_ID = `${SITE}/#website`

const LOGO = "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"

const postalAddress = (address) => ({
    "@type": "PostalAddress",
    streetAddress: address.streetAddress,
    addressLocality: address.addressLocality,
    addressRegion: address.addressRegion,
    postalCode: address.postalCode,
    addressCountry: address.addressCountry,
})

const geoCoordinates = (geo) => ({
    "@type": "GeoCoordinates",
    latitude: geo.latitude,
    longitude: geo.longitude,
})

export const localBusinessNode = Object.freeze({
    "@type": "ProfessionalService",
    "@id": LOCAL_BUSINESS_ID,
    name: businessName,
    legalName: legalName,
    url: `${SITE}/`,
    logo: LOGO,
    image: LOGO,
    telephone: hqPhone,
    priceRange: "$$",
    areaServed: { "@type": "Country", name: "Singapore" },
    // Primary address is the Paya Lebar branch: it is the address on the
    // Google Business Profile, and NAP consistency with GBP is what drives
    // local ranking. The Ubi registered office is secondary.
    address: postalAddress(branchAddress),
    geo: geoCoordinates(branchGeo),
    location: [
        {
            "@type": "Place",
            name: "StringsSG Singapore - Paya Lebar",
            address: postalAddress(branchAddress),
            geo: geoCoordinates(branchGeo),
        },
        {
            "@type": "Place",
            name: "StringsSG Pte Ltd - Registered Office",
            address: postalAddress(hqAddress),
            geo: geoCoordinates(hqGeo),
        },
    ],
    openingHoursSpecification: [
        {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "08:00",
            closes: "22:00",
        },
    ],
    sameAs: socialProfiles,
})

/**
 * The company behind the site, under the ORGANIZATION_ID that every
 * BlogPosting author/publisher already points at. Aircon pages referenced
 * that @id for years without any page defining it, so it resolved to nothing.
 *
 * Only facts that can be checked against ACRA / the live site: the UEN, and
 * the founding year, which is the registration year encoded in the UEN's
 * first four digits.
 */
export const organizationNode = Object.freeze({
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: businessName,
    legalName: legalName,
    url: `${SITE}/`,
    logo: { "@type": "ImageObject", url: LOGO },
    identifier: { "@type": "PropertyValue", propertyID: "UEN", value: uen },
    foundingDate: uen.slice(0, 4),
    sameAs: socialProfiles,
})

export const websiteNode = Object.freeze({
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: `${SITE}/`,
    name: businessName,
    inLanguage: "en-SG",
    publisher: { "@id": ORGANIZATION_ID },
})

// "S$84.00 - S$110.00" -> { lowPrice: "84", highPrice: "110" }
const parsePriceRange = (priceRange) => {
    const matches = String(priceRange).match(/[\d.]+/g)
    if (!matches || matches.length < 2) return {}
    return {
        lowPrice: String(Number(matches[0])),
        highPrice: String(Number(matches[matches.length - 1])),
    }
}

/**
 * A page-specific Service, provided by the one business above.
 *
 * `pageUrl` should have no trailing slash - the @id is derived from it.
 */
export const buildServiceNode = ({
    pageUrl,
    name,
    description,
    serviceType,
    priceRange,
    image,
}) => {
    const node = {
        "@type": "Service",
        "@id": `${pageUrl}/#service`,
        name,
        url: pageUrl,
        serviceType: serviceType || name,
        areaServed: { "@type": "Country", name: "Singapore" },
        provider: { "@id": LOCAL_BUSINESS_ID },
    }
    if (description) node.description = description
    if (image) node.image = image
    if (priceRange) {
        node.offers = {
            "@type": "AggregateOffer",
            priceCurrency: "SGD",
            ...parsePriceRange(priceRange),
            availability: "https://schema.org/InStock",
        }
    }
    return node
}
